import { Button, useEscapeKey, useFocusWithin } from '@popsure/dirty-swan';
import classNames from 'classnames';
import { ReactNode, useCallback, useRef } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import styles from './style.module.scss';

interface DropdownProps {
  onOutsideClick: () => void;
  children: ReactNode;
}

interface DropdownListProps {
  children: ReactNode;
  isExpanded: boolean;
  onClose: () => void;
}

export interface DropdownActionProps {
  text: string;
  isDangerous: boolean;
  onClick: () => void;
}

export const DropdownAction = ({
  text,
  isDangerous,
  onClick,
  ...props
}: DropdownActionProps) => {
  return (
    <Button
      className={classNames(
        styles.dropdownItem,
        'd-flex jc-start fw-normal w100',
        {
          [styles.dropdownItemDanger]: isDangerous,
        }
      )}
      variant="textColor"
      onClick={onClick}
      {...props}
    >
      {text}
    </Button>
  );
};

export const DropdownList = ({
  isExpanded,
  onClose,
  children,
}: DropdownListProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleOnFocusWithinChange = useCallback(
    (isFocusWithinMenu: boolean) => {
      if (!isExpanded) {
        return;
      }

      if (!isFocusWithinMenu) {
        onClose();
      }
    },
    [isExpanded, onClose]
  );

  useEscapeKey(onClose);
  useFocusWithin(ref?.current, handleOnFocusWithinChange);

  return (
    <div
      ref={ref}
      className={classNames({
        [styles.dropdown]: true,
        [styles['dropdown--expanded']]: isExpanded,
      })}
    >
      {children}
    </div>
  );
};

export const Dropdown = ({ onOutsideClick, children }: DropdownProps) => {
  return (
    <OutsideClickHandler onOutsideClick={onOutsideClick}>
      {children}
    </OutsideClickHandler>
  );
};
